import { Link } from "@tanstack/react-router"
import type { ReactNode } from "react"

import type { ModeStripe } from "../../journal/journal-mode"
import { cx } from "../../lib/cx"
import { EmptyState } from "../../ui/empty-state"
import { StatusStripe } from "../../ui/status-stripe"
import styles from "./master-frame.module.css"
import "./master-tokens.css"

/**
 * Рамка экранов мастера: полоса состояния, рабочая область и нижние вкладки.
 *
 * ПОЧЕМУ У МАСТЕРА СВОЯ ОБОЛОЧКА, А НЕ ОБЩАЯ AppShell. Для мастера телефон — не «ещё
 * одно устройство», а единственное (ТЗ §5.1), и набор экранов у него другой: «Мой день»,
 * «Неделя», «Мои клиенты», «Профиль». Общая оболочка строит навигацию из разделов
 * кабинета (Журнал, Клиенты, Каталог…) — это навигация администратора за стойкой,
 * и мастеру она не нужна ни одним пунктом. Две навигации на одном экране были бы хуже
 * любой из них, поэтому экраны мастера живут по своему адресу /my и в своей рамке.
 *
 * ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО. В макете сверху нарисованы часы телефона и значок сети —
 * это системная строка устройства, а не наш интерфейс. Нарисовать её значило бы показать
 * человеку второе, отстающее время рядом с настоящим.
 *
 * ВКЛАДКА ВЫДЕЛЕНА НЕ ТОЛЬКО ЦВЕТОМ: у активной есть полоска-индикатор и aria-current.
 * Цвет в одиночку смысла не несёт — это общее правило библиотеки элементов.
 */

export type MasterTab = "day" | "week" | "clients" | "profile"

interface TabDescriptor {
  readonly id: MasterTab
  readonly label: string
  readonly to: "/my/day" | "/my/week" | "/my/clients" | "/my/profile"
}

const TABS: readonly TabDescriptor[] = [
  { id: "day", label: "Мой день", to: "/my/day" },
  { id: "week", label: "Неделя", to: "/my/week" },
  { id: "clients", label: "Мои клиенты", to: "/my/clients" },
  { id: "profile", label: "Профиль", to: "/my/profile" },
]

export function MasterFrame(props: {
  readonly active: MasterTab
  /** Полоса «нет связи» или «технические работы». null — всё в порядке. */
  readonly stripe?: ModeStripe | null | undefined
  readonly onStripeAction?: (() => void) | undefined
  readonly children: ReactNode
}): ReactNode {
  const stripe = props.stripe ?? null

  return (
    <div className={styles.frame}>
      {stripe === null ? null : (
        <StatusStripe
          tone={stripe.tone === "offline" ? "offline" : "billing"}
          title={stripe.title}
          text={stripe.text}
          {...(props.onStripeAction === undefined
            ? {}
            : { action: { label: stripe.actionLabel, onClick: props.onStripeAction } })}
        />
      )}

      <main className={styles.body}>{props.children}</main>

      <nav className={styles.tabbar} aria-label="Экраны мастера">
        {TABS.map((tab) => (
          <Link
            key={tab.id}
            to={tab.to}
            search={{}}
            className={cx(styles.tab, tab.id === props.active ? styles.tabActive : undefined)}
            aria-current={tab.id === props.active ? "page" : undefined}
          >
            <span className={styles.tabMark} aria-hidden="true" />
            <span className={styles.tabLabel}>{tab.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  )
}

/**
 * Шапка экрана мастера: заголовок, подпись и место для действия справа.
 *
 * Вынесена отдельно, потому что повторяется на всех четырёх экранах, а «кто следующий»
 * обязан читаться без прокрутки: любая лишняя строка в шапке съедает первую карточку.
 */
export function MasterHead(props: {
  readonly title: string
  readonly subtitle?: string | undefined
  readonly actions?: ReactNode
}): ReactNode {
  return (
    <div className={styles.head}>
      <div className={styles.headText}>
        <h1 className={styles.headTitle}>{props.title}</h1>
        {props.subtitle === undefined ? null : (
          <p className={styles.headSubtitle}>{props.subtitle}</p>
        )}
      </div>
      {props.actions === undefined ? null : <div className={styles.headActions}>{props.actions}</div>}
    </div>
  )
}

/**
 * Человек вошёл, но карточки сотрудника у него нет.
 *
 * ЭТО СОСТОЯНИЕ ДОСТУПА, А НЕ ПУСТОЙ ДЕНЬ, и одно на все экраны мастера: без карточки
 * сотрудника у человека нет ни своей колонки в журнале, ни своих клиентов, ни времени,
 * в которое его можно записать. Показывать ему каркас загрузки, который никогда
 * не закончится, — худшее из возможного: экран выглядит сломанным, а сломана учётная
 * запись, и чинит её администратор.
 */
export function MasterNoEmployee(props: { readonly title: string }): ReactNode {
  return (
    <>
      <MasterHead title={props.title} />
      <EmptyState
        className={styles.notEmployee}
        title="Вас ещё не завели мастером"
        text="Эти экраны собираются из ваших записей. Карточку мастера заводит администратор салона — как только она появится, здесь будет ваше расписание."
      />
    </>
  )
}

/** Строка возврата: стрелка 44×44 и подпись, куда она ведёт. */
export function MasterBackBar(props: {
  readonly to: "/my/day" | "/my/clients" | "/my/week"
  readonly label: string
  readonly right?: ReactNode
}): ReactNode {
  return (
    <div className={styles.backBar}>
      <Link to={props.to} search={{}} className={styles.back} aria-label={`Назад: ${props.label}`}>
        <span aria-hidden="true">‹</span>
      </Link>
      <span className={styles.backLabel}>{props.label}</span>
      {props.right === undefined ? null : <div className={styles.backRight}>{props.right}</div>}
    </div>
  )
}
